import React from "react";

import { Container, H, LoadingButton } from "components";
import { PropsWithChildren } from "react";
import { ErrorMessage } from "../ErrorMessage";
import { AdminFormProps } from "./models";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useConfirmationModal } from "../confirmation-modal/ConfirmationModal";
import { useDispatch } from "react-redux";
import { pushNotification } from "domain/store/root";

import './AdminForm.scss';

const FORM_TYPES = {
  "CREATE": {
    className: "admin-form-create",
    button: "create",
    afterSaveURL: "./",
    deleteButton: "delete",
    afterDeleteURL: "./",
  },
  "EDIT": {
    className: "admin-form-edit",
    button: "save",
    afterSaveURL: "../",
    deleteButton: "delete",
    afterDeleteURL: "./",
  },
}
type InternalAdminFormProps = PropsWithChildren<Pick<AdminFormProps<any>, 'error' | 'className'>>;
type CustomFormProps = {
  deleteButtonText?: string;
};

type FormProps = PropsWithChildren<{ 
  title?: string;
  buttons?: React.ReactNode | undefined;
  custom?: CustomFormProps;
  onSave?() : Promise<Response> | undefined;
  onDelete?() : Promise<Response> | undefined;
  onDisable?() : Promise<Response> | undefined;
}>;

type AbstractFormProps = { type: "CREATE" | "EDIT" } & FormProps;

export const AdminForm = ({ error, children, className }: InternalAdminFormProps ) => {

  return (
    <Container className={`admin-form ${className}`}>
      { children }
      { error && <ErrorMessage error={ error }></ErrorMessage> }
    </Container>);
}

export const AdminCreateForm = (props: FormProps) => {
  return <AbstractAdminForm type="CREATE" { ...props} />
};

export const AdminEditForm = (props: FormProps) => {
  return <AbstractAdminForm type="EDIT" { ...props} />
};

export const saveWithRevalidate = (onSave: () => Promise<Response> | undefined, revalidate: () => {}) => {
  
  return async (): Promise<any> => {
    try {
      const result = await onSave();
      revalidate();
      return result;
    } catch (error) {
      throw error;
    }
  };

}

const AbstractAdminForm = (
  { children, buttons, title, type, custom, onSave, onDelete }: AbstractFormProps) => {

  const className  = FORM_TYPES[type].className;
  const buttonText = FORM_TYPES[type].button;
  const afterSaveURL = FORM_TYPES[type].afterSaveURL;
  const deleteButtonText = FORM_TYPES[type].deleteButton;
  const afterDeleteURL = FORM_TYPES[type].afterDeleteURL;

  const { t } = useTranslation(['common', 'admin', 'errors']);
  const history = useHistory();
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();


  const showError = (message?: string) => {
    dispatch(
      pushNotification({
        variant: 'error',
        message: message || t('errors:common.unexpected'),
        title: 'Error',
      })
    );
  };

  const showSuccess = () => {
    dispatch(
      pushNotification({
        variant: 'success',
        message: t('common:messages.success'),
        title: 'Success',
      })
    );
  }

  const {
    element,
    openModal,
  } = useConfirmationModal({
    onAccept: () => handleAction(onDelete, afterDeleteURL),
    onCancel: () => { }  // Do nothing
  });
  
  const handleOnSave = () => {
    handleAction(onSave, afterSaveURL); 
  }

  const handleOnDelete = () => {
    openModal();
  }
  
  const handleAction = (fn: typeof onSave, afterURL: string) => {
    if (fn === undefined) {
      throw new Error('Function called, but is undefined');
    }
    setLoading(true);
    const result = fn();
    if (!result) {
      setLoading(false);
      return;
    }
    result.then(async (res: any) => {
      // Check res status
      const nok = res.status >= 400;
      if (nok) {
        // Show notification
        // Check if error is json and has message
        let message: string | undefined = undefined;
        try {
          const json = await res.json();
          if (json.message) {
            message = json.message;
          }
        } catch (error) {

          // Check if i18n message is present for the current status code
          const i18nMessage = t(`errors:common.status.${res.status}`);
          if (i18nMessage !== `common.status.${res.status}`) {
            message = i18nMessage;
          }
        }
        showError(message);
        setLoading(false);
        return;
      }
      setLoading(false);
      showSuccess();
      history.push(afterDeleteURL);
    }).catch(err => {
      setLoading(false);
      console.error(err);
    })
  }

  return (
    <Container className={`${className}`}>
      {element}
    { title && <H.H4>{ title }</H.H4>}
      { children }
      <Container className={`${className}-buttons`}>
        { buttons || (
          <>
            { onSave && <LoadingButton onClick={handleOnSave} loading={loading}>{t(`common:actions.${buttonText}`)}</LoadingButton> }
            { onDelete && 
              <LoadingButton onClick={handleOnDelete} className='danger' loading={loading}>
                {t(custom?.deleteButtonText || `common:actions.${deleteButtonText}`)}
              </LoadingButton> }
          </>) }
      </Container>
    </Container>);
}
